import type { DragEndEvent, DragOverEvent } from '@dnd-kit/react';
import type { UserLocalStorage } from '../../types/localStorage';
import type { customizableDashboardData } from './CustomizableDashboard/CustomizableDashboardState';

import { DragDropProvider } from '@dnd-kit/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { putUserConfig } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import Loader from '../../components/Loader/Loader';
import TopbarButton from '../../components/Topbar/buttons/base/TopbarButton';
import DashboardCustomizationSidebar from './components/DashboardCustomizationSidebar/DashboardCustomizationSidebar';
import DisabledPluginSection from './components/DisabledPluginSection/DisabledPluginSection';
import DraggableCard from './components/DraggableCard/DraggableCard';
import NotificationsTopbarButton from './components/NotificationsTopbarButton/NotificationsTopbarButton';
import TrialEndedPill from './components/TrialEndedPill/TrialEndedPill';

import { useNotifications } from '../../hooks/api/useNotifications';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { defaultButtonsProps, useModals } from '../../contexts/ModalContext';

import { MagnifierSolidIcon } from '../../images/shapes';

import PageLayout from '../../layout/PageLayout/PageLayout';
import {
  customizableDashboardReducer,
  initializeCustomizableDashboardState,
  removeDashboardSection,
} from './CustomizableDashboard/CustomizableDashboardState';
import DashboardIntro from './sections/DashboardIntro';
import { defaultSections } from './sections/DefaultSections';
import useDashboardSectionRenderers from './sections/useDashboardSectionRenderers';

const testId = 'home';
const discardDashboardChangesModalId = 'discard-dashboard-changes';
const saveDashboardChangesModalId = 'save-dashboard-changes';
const smallScreenDashboardEditingModalId = 'small-screen-dashboard-editing';

const PAID_PILL_CLASSES = [
  'bg-blue-50 border-blue-200 text-blue-600',
  'dark:bg-blue-900 dark:border-blue-800 dark:text-blue-200',
];

const FREE_PILL_CLASSES = [
  'bg-slate-50 border-slate-200 text-slate-600',
  'dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300',
];

const Home = () => {
  const { t } = useTranslation();
  const modal = useModals();
  const jwt = useToken();
  const { spaceLimits, spaceData, space } = useSelectedSpace();
  const globalSearch = useGlobalSearch({ testId });
  const [userStorage, setUserStorage] =
    useLocalStorageState<UserLocalStorage>('cms.user');
  const [, setSidebarOpen] = useLocalStorageState<boolean>(
    'cms.user_sideBarOpen',
  );

  const { notifications, notificationsLoading, onNotificationsRead } =
    useNotifications();

  const isSmallScreen = useMediaQuery({
    query: '(max-width: 1024px)',
  });
  const [editDashboard, setEditDashboard] = useState(false);
  const [isSavingDashboard, setIsSavingDashboard] = useState(false);
  const previousSmallScreen = useRef(isSmallScreen);

  const [sections, setSections] =
    useLocalStorageState<customizableDashboardData>(
      `cms.user.config.dashboardSections.${space}`,
      {
        defaultValue: () => defaultSections,
      },
    );

  const persistedSections = space
    ? userStorage?.data?.config?.dashboardSections?.[space]
    : undefined;

  useEffect(() => {
    if (persistedSections) setSections(persistedSections);
  }, [persistedSections, setSections]);

  const { renderers: sectionComponents, sections: registeredSections } =
    useDashboardSectionRenderers(testId);

  const todoList = userStorage?.data?.config?.todo;
  const showFirstSteps =
    todoList?.contentType?.status !== 'done' ||
    todoList?.contentObject?.status !== 'done' ||
    todoList?.publicDoc?.status !== 'done';

  const loadedSections = persistedSections ?? sections;
  const [dashboardSource, setDashboardSource] = useState({
    space,
    sections: loadedSections,
  });
  const [dashboardSections, dispatch] = useReducer(
    customizableDashboardReducer,
    loadedSections,
    (initialSections) =>
      initializeCustomizableDashboardState(initialSections, registeredSections),
  );

  if (
    dashboardSource.space !== space ||
    dashboardSource.sections !== loadedSections
  ) {
    setDashboardSource({ space, sections: loadedSections });
    dispatch({
      type: 'reset',
      data: loadedSections,
      registeredSections,
    });
    setEditDashboard(false);
  }

  const displayableSectionsOrder = useMemo(
    () =>
      dashboardSections.sectionsOrder.filter(
        (sectionId) => showFirstSteps || sectionId !== 'first-steps',
      ),
    [dashboardSections.sectionsOrder, showFirstSteps],
  );

  const visibleSections = useMemo(
    () =>
      displayableSectionsOrder.filter(
        (sectionId) => dashboardSections.sections[sectionId].visible,
      ),
    [dashboardSections.sections, displayableSectionsOrder],
  );

  const activeSections = visibleSections.map((sectionId, index) => ({
    id: sectionId,
    index,
    label: dashboardSections.sections[sectionId].label,
    onClick: () => hideSection(sectionId),
  }));

  const availableSections = displayableSectionsOrder
    .filter((sectionId) => !dashboardSections.sections[sectionId].visible)
    .map((sectionId, index) => ({
      id: sectionId,
      index,
      label: dashboardSections.sections[sectionId].label,
      onClick: () => showSection(sectionId),
    }));

  const hideSection = (sectionId: string) => {
    dispatch({ type: 'hide', key: sectionId });
  };

  const showSection = (sectionId: string) => {
    dispatch({ type: 'show', key: sectionId });
  };

  const rollbackAndCloseDashboardEditing = useCallback(() => {
    dispatch({ type: 'rollback' });
    setEditDashboard(false);
  }, []);

  const closeDashboardEditing = useCallback(async () => {
    if (!dashboardSections.dirty) {
      rollbackAndCloseDashboardEditing();
      return;
    }

    const shouldDiscardChanges = await modal.confirmation(
      t('Home.DashboardCustomization.LeaveDescription'),
      t('Home.DashboardCustomization.LeaveTitle'),
      t('Home.DashboardCustomization.DiscardChanges'),
      t('Global.Cancel'),
      'warning',
      { id: discardDashboardChangesModalId },
    );

    if (shouldDiscardChanges) rollbackAndCloseDashboardEditing();
  }, [dashboardSections.dirty, modal, rollbackAndCloseDashboardEditing, t]);

  const toggleEditDashboard = useCallback(() => {
    if (isSmallScreen) return;

    if (editDashboard) {
      closeDashboardEditing();
      return;
    }

    setSidebarOpen(true);
    setEditDashboard(true);
  }, [closeDashboardEditing, editDashboard, isSmallScreen, setSidebarOpen]);

  const persistDashboardData = useCallback(
    async (dashboardData: customizableDashboardData) => {
      const spaceId = space;

      if (!userStorage?.data?.id || !spaceId) {
        toast.error(t('Form.CommunicationErrorMessage'));
        return false;
      }

      const newConfig = {
        ...userStorage.data.config,
        dashboardSections: {
          ...userStorage.data.config.dashboardSections,
          [spaceId]: dashboardData,
        },
      };

      setIsSavingDashboard(true);

      try {
        const { body, status, ok } = await putUserConfig(jwt, undefined, {
          id: userStorage.data.id,
          config: newConfig,
        });

        if (!ok) throw parseResponseErrors(body, status);

        setUserStorage({
          ...userStorage,
          data: {
            ...userStorage.data,
            config: newConfig,
          },
        });
        setSections(dashboardData);
        return true;
      } catch {
        toast.error(t('Form.CommunicationErrorMessage'));
        return false;
      } finally {
        setIsSavingDashboard(false);
      }
    },
    [jwt, setSections, setUserStorage, space, t, userStorage],
  );

  const removeSection = useCallback(
    async (sectionId: string) => {
      if (isSavingDashboard) return;

      const saved = await persistDashboardData(
        removeDashboardSection(dashboardSections.originalState, sectionId),
      );

      if (saved) dispatch({ type: 'remove', key: sectionId });
    },
    [dashboardSections.originalState, isSavingDashboard, persistDashboardData],
  );

  const saveDashboardEditing = useCallback(
    async (skipConfirmation = false) => {
      if (isSavingDashboard) return;

      if (!skipConfirmation) {
        const shouldSaveChanges = await modal.confirmation(
          t('Home.DashboardCustomization.SaveDescription'),
          t('Home.DashboardCustomization.SaveTitle'),
          t('Global.SaveChanges'),
          t('Global.Cancel'),
          'simple',
          { id: saveDashboardChangesModalId },
        );

        if (!shouldSaveChanges) return;
      }

      const saved = await persistDashboardData({
        sections: dashboardSections.sections,
        sectionsOrder: dashboardSections.sectionsOrder,
      });

      if (!saved) return;

      dispatch({ type: 'commit' });
      setEditDashboard(false);
    },
    [dashboardSections, isSavingDashboard, modal, persistDashboardData, t],
  );

  useEffect(() => {
    const enteredSmallScreen = !previousSmallScreen.current && isSmallScreen;

    previousSmallScreen.current = isSmallScreen;

    if (
      !enteredSmallScreen ||
      !editDashboard ||
      isSavingDashboard ||
      modal.isOpen(smallScreenDashboardEditingModalId)
    ) {
      return;
    }
    setEditDashboard(false);

    modal({
      id: smallScreenDashboardEditingModalId,
      title: t('Home.DashboardCustomization.SmallScreenTitle'),
      content: t('Home.DashboardCustomization.SmallScreenDescription'),
      buttons: [
        {
          ...defaultButtonsProps.cancel,
          label: t('Home.DashboardCustomization.DiscardChanges'),
          onClick: () => {
            if (dashboardSections.dirty) {
              dispatch({ type: 'rollback' });
            }
          },
        },
        {
          ...defaultButtonsProps.save,
          label: t('Global.SaveChanges'),
          onClick: () => {
            saveDashboardEditing(true);
          },
        },
      ],
    });
  }, [
    dashboardSections.dirty,
    editDashboard,
    isSavingDashboard,
    isSmallScreen,
    modal,
    saveDashboardEditing,
    t,
  ]);

  const onDragEnd = useCallback((event: DragEndEvent) => {
    dispatch({
      type: 'swap',
      event,
    });
  }, []);

  const onSidebarDragOver = useCallback((event: DragOverEvent) => {
    dispatch({
      type: 'move-between-groups',
      event,
    });
  }, []);

  return (
    <>
      {isSavingDashboard && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center bg-white/80
          backdrop-blur-sm dark:bg-slate-900/80"
          aria-label={t('Home.DashboardCustomization.Saving')}
        >
          <Loader type="spinner-grid" size="big" />
        </div>
      )}
      <PageLayout
        page="dashboard"
        title={spaceData?.name || t('Global.Dashboard')}
        buttons={
          <>
            <TrialEndedPill
              className="hidden md:flex md:ml-4"
              {...getTestProps(testId, 'desktop', 'testId')}
            />
            <div
              className={twMerge(
                'flex items-center h-6 px-5 rounded-full whitespace-nowrap mx-2 md:mr-4 md:ml-0',
                'font-medium text-base uppercase tracking-none leading-none border',
                spaceLimits?.price ? PAID_PILL_CLASSES : FREE_PILL_CLASSES,
              )}
              {...getTestProps(testId, 'space-plan-pill')}
            >
              {spaceLimits?.visible_name ?? spaceLimits?.name ?? 'Free'}
            </div>
            <DashboardCustomizationSidebar
              activeSections={activeSections}
              availableSections={availableSections}
              dirty={dashboardSections.dirty}
              isOpen={editDashboard}
              onDragOver={onSidebarDragOver}
              onClose={closeDashboardEditing}
              onSave={() => saveDashboardEditing()}
            />
            <TopbarButton
              tooltip={t('Global.ContentSearch.Tooltip')}
              iconPosition="start"
              iconImage={<MagnifierSolidIcon className="w-4 h-4" />}
              tooltipPlacement="leftCenter"
              phoneTooltipPlacement="leftBottom"
              onClick={globalSearch}
              additionalIconClasses="w-4 min-w-4"
              buttonColor="transparent"
              additionalClasses="w-11 h-11 xl:h-8 xl:w-8"
            />
            <NotificationsTopbarButton
              notifications={notifications}
              notificationsLoading={notificationsLoading}
              onNotificationsRead={onNotificationsRead}
              {...getTestProps(testId, 'notifications', 'testId')}
            />
          </>
        }
        buttonsMobilePlacement="top"
        testId={testId}
      >
        <TrialEndedPill
          className="md:hidden"
          {...getTestProps(testId, 'mobile', 'testId')}
        />
        <div
          className="mt-6 lg:mt-4 mb-12 space-y-6"
          {...getTestProps(testId, 'sections')}
        >
          <DashboardIntro
            toggleEditDashboard={toggleEditDashboard}
            editDisabled={isSmallScreen}
            testId={testId}
          />
          <DragDropProvider onDragEnd={onDragEnd}>
            {visibleSections.map((id) => (
              <DraggableCard
                key={id}
                id={id}
                hideSection={hideSection}
                index={dashboardSections.sectionsOrder.indexOf(id)}
                dashboardEditing={editDashboard}
              >
                {sectionComponents[id] ?? (
                  <DisabledPluginSection
                    pluginName={dashboardSections.sections[id].label}
                    onRemove={() => removeSection(id)}
                  />
                )}
              </DraggableCard>
            ))}
          </DragDropProvider>
        </div>
      </PageLayout>
    </>
  );
};

export default Home;
